import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import { ProductsDropdown } from "@/components/products/ProductsDropdown";
import { Accordion } from "@/components/ui";
import { ProgressCard, SummaryStatsCards, UsageBarChart, UsageFilters, UsagePieChart, UsagePlanOverview, useUsageFilters } from "@/components/usage";
import { USAGE_VIEW_TYPES } from "@/constants/usage";
import { FILTER_VALUES } from "@/constants/filters";
import useAuthStore from "@/context/auth/useAuthStore";
import { useApiKeys } from "@/hooks/api-keys";
import { useEffectiveCustomerId } from "@/hooks/useEffectiveCustomerId";
import { useInvoices } from "@/hooks/useInvoices";
import { useUsageContractSelection } from "@/hooks/useUsageContractSelection";
import { useUsagePlan } from "@/hooks/useUsagePlan";
import { useMeteredUsage } from "@/hooks/usage";
import { DashboardPageFilters, DashboardPageLayout, DashboardPageLoading, DashboardPageSection } from "@/layouts/DashboardPageLayout";
import { getConsumptionDateRange } from "@/utils/usageDateRange";
export default function UsagePage() {
    const { productCode } = useAuthStore();
    const { isViewingAllOrgs } = useEffectiveCustomerId();
    const [selectedContractId, setSelectedContractId] = useState("");
    const contractSelection = useUsageContractSelection({
        authProductCode: productCode ?? undefined,
        selectedContractId,
        setSelectedContractId
    });
    const { filters, setViewType, setSelectedDate, setDateRange, setSelectedApiKey, updateFilters } = useUsageFilters();
    const activeProductCode = contractSelection.selectedContract?.ProductCode ?? "";
    const resolvedContractId = contractSelection.resolvedSelectedContractId;
    const selectedContractExpiryDate = contractSelection.selectedContract?.ExpiryDate;
    const selectedContractExpiryTime = selectedContractExpiryDate ? new Date(selectedContractExpiryDate).getTime() : Number.NaN;
    const historicalEndDate = Number.isNaN(selectedContractExpiryTime) ? undefined : selectedContractExpiryDate?.slice(0, 10);
    const selectedContractStartDate = contractSelection.selectedContract?.StartDate;
    const customerIdOverride = isViewingAllOrgs ? contractSelection.selectedContract?.CustomerId : undefined;
    const [initializedDateContractId, setInitializedDateContractId] = useState("");
    const usageContractId = initializedDateContractId === resolvedContractId ? resolvedContractId : "";
    const { usagePlan, isLoadingContracts } = useUsagePlan({
        contractId: resolvedContractId
    });
    useEffect(()=>{
        if (!resolvedContractId) {
            setInitializedDateContractId("");
            return;
        }
        if (initializedDateContractId === resolvedContractId) {
            return;
        }
        const endDate = contractSelection.selectedIsExpired && historicalEndDate ? historicalEndDate : new Date().toISOString().slice(0, 10);
        const start = new Date(`${endDate}T00:00:00Z`);
        start.setUTCDate(start.getUTCDate() - 7);
        let startDate = start.toISOString().slice(0, 10);
        if (selectedContractStartDate && startDate < selectedContractStartDate.slice(0, 10)) {
            startDate = selectedContractStartDate.slice(0, 10);
        }
        if (startDate > endDate) {
            startDate = endDate;
        }
        updateFilters({
            selectedApiKey: FILTER_VALUES.ALL,
            selectedDate: endDate,
            startDate,
            endDate
        });
        setInitializedDateContractId(resolvedContractId);
    }, [
        contractSelection.selectedIsExpired,
        initializedDateContractId,
        resolvedContractId,
        historicalEndDate,
        selectedContractStartDate,
        updateFilters
    ]);
    // Derive chart date range from filters
    const chartStartDate = filters.viewType === USAGE_VIEW_TYPES.HOURLY ? filters.selectedDate : filters.startDate;
    const chartEndDate = filters.viewType === USAGE_VIEW_TYPES.HOURLY ? filters.selectedDate : filters.endDate;
    // Fetch metered usage for the pie chart (all keys breakdown)
    const { data: pieChartData, isLoading: pieChartLoading, error: pieChartError } = useMeteredUsage(usageContractId, chartStartDate, chartEndDate, undefined);
    const { data: apiKeysData, isLoading: apiKeysLoading } = useApiKeys(activeProductCode, !!activeProductCode, customerIdOverride);
    const { invoices, isLoading: invoicesLoading } = useInvoices(undefined, !!resolvedContractId && !contractSelection.selectedIsExpired);
    // Filter to only active API keys for display
    const activeApiKeys = useMemo(()=>apiKeysData?.items?.filter((key)=>key.status === "Active") ?? [], [
        apiKeysData?.items
    ]);
    const selectableApiKeys = useMemo(()=>contractSelection.selectedIsExpired ? apiKeysData?.items ?? [] : activeApiKeys, [
        activeApiKeys,
        apiKeysData?.items,
        contractSelection.selectedIsExpired
    ]);
    useEffect(()=>{
        if (!apiKeysLoading && filters.selectedApiKey !== FILTER_VALUES.ALL) {
            const apiKeyIsSelectable = selectableApiKeys.some((key)=>key.id === filters.selectedApiKey);
            if (!apiKeyIsSelectable) {
                setSelectedApiKey(FILTER_VALUES.ALL);
            }
        }
    }, [
        apiKeysLoading,
        selectableApiKeys,
        filters.selectedApiKey,
        setSelectedApiKey
    ]);
    const consumptionDateRange = useMemo(()=>{
        const contractInvoices = invoices.filter((invoice)=>invoice.contractId === resolvedContractId);
        return getConsumptionDateRange(contractInvoices, contractSelection.selectedIsExpired ? historicalEndDate : undefined, contractSelection.selectedContract?.StartDate);
    }, [
        contractSelection.selectedContract,
        contractSelection.selectedIsExpired,
        historicalEndDate,
        invoices,
        resolvedContractId
    ]);
    const { data: consumptionUsageData, isLoading: consumptionLoading } = useMeteredUsage(usageContractId, consumptionDateRange.startDate, consumptionDateRange.endDate, undefined, !!resolvedContractId && !contractSelection.selectedIsExpired);
    const currentApiKeysUsed = activeApiKeys.length;
    // Use consumption-specific data for the progress card
    const totalConsumptionUsed = consumptionUsageData?.totalUsageValue || 0;
    const consumptionUsageUnit = consumptionUsageData?.usageUnit || "bytes";
    const isInitializingDates = !!resolvedContractId && !usageContractId;
    const hasContractOptions = contractSelection.activeOptions.length > 0 || contractSelection.expiredOptions.length > 0;
    const isResolvingContract = hasContractOptions && !resolvedContractId;
    // Check if any data is still loading
    const isLoading = isLoadingContracts || isInitializingDates || apiKeysLoading || consumptionLoading || invoicesLoading;
    return /*#__PURE__*/ _jsxs(DashboardPageLayout, {
        title: "Usage Analytics",
        description: "Monitor API consumption patterns and usage plan limits",
        children: [
            /*#__PURE__*/ _jsx(DashboardPageFilters, {
                children: /*#__PURE__*/ _jsx("div", {
                    className: "w-full max-w-xs",
                    children: /*#__PURE__*/ _jsx(ProductsDropdown, {
                        options: contractSelection.activeOptions,
                        error: contractSelection.error,
                        expiredOptions: contractSelection.expiredOptions,
                        isLoading: contractSelection.isLoading,
                        optionType: "contract",
                        showAllOption: false,
                        value: selectedContractId,
                        onChange: setSelectedContractId
                    })
                })
            }),
            (contractSelection.isLoading || isResolvingContract || isLoading) && /*#__PURE__*/ _jsx(DashboardPageLoading, {}),
            !contractSelection.isLoading && !contractSelection.error && !isResolvingContract && !resolvedContractId && /*#__PURE__*/ _jsx(DashboardPageSection, {
                className: "py-12 text-center",
                children: /*#__PURE__*/ _jsx("p", {
                    className: "text-gray-500",
                    children: "No metered contracts are available for usage analytics."
                })
            }),
            resolvedContractId && !isLoading && !usagePlan && !isViewingAllOrgs && /*#__PURE__*/ _jsx(DashboardPageSection, {
                className: "py-12 text-center",
                children: /*#__PURE__*/ _jsx("p", {
                    className: "text-gray-500",
                    children: "No contract data available. Please contact support to set up your usage plan."
                })
            }),
            resolvedContractId && !isLoading && (usagePlan || isViewingAllOrgs) && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    activeApiKeys.length > 0 && /*#__PURE__*/ _jsx(Accordion, {
                        title: "Active API Keys",
                        badge: activeApiKeys.length,
                        defaultOpen: false,
                        children: /*#__PURE__*/ _jsx("div", {
                            className: "space-y-2",
                            children: activeApiKeys.map((apiKey)=>/*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-center justify-between p-3 bg-gray-50 rounded",
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "flex items-center gap-3",
                                            children: [
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: "w-2 h-2 bg-green-400 rounded-full"
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "font-medium text-sm",
                                                            children: apiKey.name || "Unnamed Key"
                                                        }),
                                                        /*#__PURE__*/ _jsxs("p", {
                                                            className: "text-xs text-gray-500",
                                                            children: [
                                                                "ID: ",
                                                                apiKey.id.slice(0, 20),
                                                                "..."
                                                            ]
                                                        })
                                                    ]
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "text-xs text-gray-500",
                                            children: [
                                                "Created:",
                                                " ",
                                                new Date(apiKey.createdDate).toLocaleDateString()
                                            ]
                                        })
                                    ]
                                }, apiKey.id))
                        })
                    }),
                    /*#__PURE__*/ _jsx(UsageFilters, {
                        filters: filters,
                        onViewTypeChange: setViewType,
                        onDateChange: setSelectedDate,
                        onDateRangeChange: setDateRange,
                        onApiKeyChange: setSelectedApiKey,
                        productCode: activeProductCode,
                        includeInactiveApiKeys: contractSelection.selectedIsExpired,
                        customerIdOverride: customerIdOverride
                    }),
                    filters.selectedApiKey !== FILTER_VALUES.ALL && /*#__PURE__*/ _jsx(SummaryStatsCards, {
                        contractId: usageContractId,
                        filters: filters
                    }),
                    filters.selectedApiKey === FILTER_VALUES.ALL ? pieChartLoading ? /*#__PURE__*/ _jsxs("div", {
                        className: "bg-white shadow rounded-lg",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "px-6 py-4 border-b border-gray-200",
                                children: [
                                    /*#__PURE__*/ _jsx("h3", {
                                        className: "text-lg font-medium text-gray-900",
                                        children: "Consumption Breakdown"
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-sm text-gray-500",
                                        children: "Loading usage data..."
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "flex items-center justify-center h-64",
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: "h-48 w-48 rounded-full bg-gray-200 animate-pulse"
                                })
                            })
                        ]
                    }) : pieChartError ? /*#__PURE__*/ _jsxs("div", {
                        className: "bg-white shadow rounded-lg",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "px-6 py-4 border-b border-gray-200",
                                children: [
                                    /*#__PURE__*/ _jsx("h3", {
                                        className: "text-lg font-medium text-gray-900",
                                        children: "Consumption Breakdown"
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-sm text-gray-500",
                                        children: "Top API keys by usage"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "flex items-center justify-center h-64 text-center",
                                children: /*#__PURE__*/ _jsxs("div", {
                                    className: "text-gray-500",
                                    children: [
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-lg font-medium",
                                            children: "Failed to load usage breakdown"
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-sm mt-2",
                                            children: pieChartError.message
                                        })
                                    ]
                                })
                            })
                        ]
                    }) : pieChartData ? /*#__PURE__*/ _jsx(UsagePieChart, {
                        data: pieChartData,
                        apiKeys: selectableApiKeys
                    }) : null : /*#__PURE__*/ _jsx(UsageBarChart, {
                        contractId: usageContractId,
                        filters: filters
                    }),
                    !isViewingAllOrgs && usagePlan && /*#__PURE__*/ _jsx(UsagePlanOverview, {
                        usagePlan: usagePlan
                    }),
                    !isViewingAllOrgs && usagePlan && !contractSelection.selectedIsExpired && /*#__PURE__*/ _jsx(ProgressCard, {
                        currentApiKeysUsed: currentApiKeysUsed,
                        totalConsumptionUsed: totalConsumptionUsed,
                        usageUnit: consumptionUsageUnit,
                        usagePlan: usagePlan,
                        dateRange: consumptionDateRange
                    })
                ]
            })
        ]
    });
}
